import React from "react";
import { doNothing, useSync } from "@hex-insights/core";
import { EditMode } from "@hex-insights/forms";
import {
	CalendarEvent,
	CalendarEventResponse,
	CalendarEventResponseResponse,
	User,
	UserNameAndImageFragment,
} from "../../../../Utilities";
import { CalendarEventResponseForm } from "../../../Forms/CalendarEventResponse";

export type CalendarEventResponseWithUser = Pick<CalendarEventResponse, "id" | "response"> & {
	author: (Pick<User, "id"> & UserNameAndImageFragment) | null;
};

export type RSVPFormProps = {
	calendarEventID: CalendarEvent["id"];
	calendarEventResponse?: CalendarEventResponseWithUser | null;
	onSuccess?: CalendarEventResponseForm.ControlledCreateOnExistingCalendarEventProps["onSuccess"];
};

export function RSVPForm({ calendarEventID, calendarEventResponse, onSuccess = doNothing }: RSVPFormProps) {
	const createFormStateArgs = React.useMemo(() => ({ initialFormValues: { calendarEventID } }), [calendarEventID]);
	const fullCalendarEventResponse = React.useMemo(
		() => ({
			id: calendarEventResponse?.id ?? "0",
			response: calendarEventResponse?.response ?? CalendarEventResponseResponse.Attending,
			calendarEvent: { id: calendarEventID, name: "" },
			author: calendarEventResponse?.author ?? null,
		}),
		[calendarEventID, calendarEventResponse],
	);
	const detailFormStateArgs = React.useMemo(() => ({ editMode: EditMode.WriteOnly }), []);

	const { syncID, synchronize } = useSync();

	const onDetailSuccess = React.useCallback<
		CalendarEventResponseForm.ControlledCreateOnExistingCalendarEventProps["onSuccess"]
	>(
		(formState, data) => {
			synchronize();
			onSuccess(formState, data);
		},
		[synchronize, onSuccess],
	);

	if (calendarEventResponse) {
		return (
			<CalendarEventResponseForm.ControlledDetailOnExistingCalendarEvent
				key={syncID}
				formStateArgs={detailFormStateArgs}
				calendarEventResponse={fullCalendarEventResponse}
				onSuccess={onDetailSuccess}
				noCloseConfirmation
				noLocalStorage
				noChangesDialog
				noNavigationPrompt
				noNotifications
				autoSave
			/>
		);
	}

	return (
		<CalendarEventResponseForm.ControlledCreateOnExistingCalendarEvent
			formStateArgs={createFormStateArgs}
			onSuccess={onSuccess}
			noCloseConfirmation
			noLocalStorage
			noChangesDialog
			noNavigationPrompt
			noNotifications
			autoSave
		/>
	);
}
